import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
    Box,
    LinearProgress,
    Paper
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import PaidRoundedIcon from '@mui/icons-material/PaidRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import RecommendRoundedIcon from '@mui/icons-material/RecommendRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import { DataGrid } from '@mui/x-data-grid';
import { useLocation } from 'react-router-dom';

import BiMetric from '../../components/biMetric';
import LineChart from '../../components/lineChart';
import MonthRangePicker from '../../components/monthRangePicker';
import PageHeader from '../../components/pageHeader';
import {
    formatMonthParam,
    getCurrentMonthStart
} from '../../utils/dateUtils';
import {
    formatInteger,
    formatPercentRatio
} from '../../utils/numberUtils';
import { formatCurrency } from '../../utils/stringFormat';
import {
    buildDailyIndications,
    buildIndicationRows,
    buildIndicationSummary,
    fetchIndicacoesMes
} from './indicacoesData';
import '../css/biAluguelAccess.css';

const minMonth = new Date(2025, 0, 1);

const emptyRowsLabel = 'Nenhuma indicação encontrada para o período selecionado.';

const chartSeries = [
    {
        key: 'indicated',
        label: 'Indicações',
        peakText: (point) => `Pico de indicações: ${formatInteger(point.indicated)} em ${point.label}`,
        tooltipValue: (point) => `${formatInteger(point.indicated)} indicações`
    },
    {
        key: 'concluded',
        label: 'Concluídas',
        peakText: (point) => `Pico de concluídas: ${formatInteger(point.concluded)} em ${point.label}`,
        tooltipValue: (point) => `${formatInteger(point.concluded)} concluídas`
    }
];

const IndicacoesBIGE = () => {
    const location = useLocation();
    const isCrmRoute = location.pathname.toLowerCase().startsWith('/crm/');
    const pageTitle = isCrmRoute ? 'CRM - Indicações' : 'BI Aluguel - Indicações';

    const [dataInicio, setDataInicio] = useState(getCurrentMonthStart);
    const [dataFim, setDataFim] = useState(getCurrentMonthStart);
    const [indicacoes, setIndicacoes] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const loadIndicacoes = useCallback(async (startDate, endDate) => {
        setIsLoading(true);
        const monthIndicacoes = await fetchIndicacoesMes({
            dataInicio: formatMonthParam(startDate),
            dataFim: formatMonthParam(endDate)
        });
        setIndicacoes(monthIndicacoes);
        setIsLoading(false);
    }, []);

    useEffect(() => {
        loadIndicacoes(dataInicio, dataFim);
    }, [dataFim, dataInicio, loadIndicacoes]);

    const summary = useMemo(() => buildIndicationSummary(indicacoes), [indicacoes]);
    const dailyIndications = useMemo(
        () => buildDailyIndications(indicacoes, dataInicio, dataFim),
        [dataFim, dataInicio, indicacoes]
    );
    const rows = useMemo(() => buildIndicationRows(indicacoes), [indicacoes]);

    const handleDateRangeApply = (startDate, endDate) => {
        setDataInicio(startDate);
        setDataFim(endDate);
    };

    const columns = useMemo(() => [
        {
            field: 'clienteIndicador',
            headerName: 'Cliente indicador',
            minWidth: 260,
            flex: 1.35
        },
        {
            field: 'ticketIndicado',
            headerName: 'Ticket indicado',
            type: 'number',
            minWidth: 170,
            flex: 0.7,
            valueFormatter: (value) => formatCurrency(value)
        },
        {
            field: 'ticketConquistado',
            headerName: 'Ticket conquistado',
            type: 'number',
            minWidth: 190,
            flex: 0.75,
            valueFormatter: (value) => formatCurrency(value)
        },
        {
            field: 'totalIndicacoes',
            headerName: 'Total indicações',
            type: 'number',
            width: 170,
            align: 'center',
            headerAlign: 'center'
        },
        {
            field: 'totalIndicacoesConcluidas',
            headerName: 'Total concluídas',
            type: 'number',
            width: 170,
            align: 'center',
            headerAlign: 'center'
        },
        {
            field: 'diasBase',
            headerName: 'Dias na base',
            type: 'number',
            width: 150,
            align: 'center',
            headerAlign: 'center',
            valueFormatter: (value) => value === null ? '' : formatInteger(value)
        }
    ], []);

    return (
        <Box className='mainBox bi-aluguel-access-page bi-ge-indications-page'>
            <PageHeader>{pageTitle}</PageHeader>

            <section className='bi-aluguel-access-shell bi-ge-indications-shell'>
                {isLoading && <LinearProgress className='bi-aluguel-access-loading' />}

                <div className='bi-ge-indications-summary-row'>
                    <div className='bi-aluguel-access-filters bi-ge-indications-filters bi-ge-indications-summary-filters'>
                        <MonthRangePicker
                            startDate={dataInicio}
                            endDate={dataFim}
                            minDate={minMonth}
                            maxDate={getCurrentMonthStart()}
                            disabled={isLoading}
                            onApply={handleDateRangeApply}
                        />
                    </div>

                    <div className='bi-aluguel-access-metrics bi-ge-indications-metrics'>
                        <BiMetric
                            icon={PaymentsRoundedIcon}
                            label='Total dos tickets'
                            value={formatCurrency(summary.totalValue)}
                            subtitle={`${formatInteger(summary.totalIndications)} indicações`}
                            tone='green'
                        />
                        <BiMetric
                            icon={CheckCircleRoundedIcon}
                            label='Tickets conquistados'
                            value={formatCurrency(summary.conqueredValue)}
                            subtitle={`${formatInteger(summary.concludedIndications)} indicações concluídas`}
                            tone='blue'
                        />
                        <BiMetric
                            icon={RecommendRoundedIcon}
                            label='Conversão'
                            value={formatPercentRatio(summary.conversionRate)}
                            subtitle={`${formatInteger(summary.contractPhaseIndications)} em fase de contrato`}
                        />
                        <BiMetric
                            icon={PaidRoundedIcon}
                            label='Ticket médio'
                            value={formatCurrency(summary.averageTicket)}
                            subtitle={`${formatInteger(summary.indicatorClients)} clientes que indicaram`}
                            tone='navy'
                        />
                    </div>
                </div>

                <LineChart
                    ariaLabel='Indicações recebidas e concluídas por dia'
                    eyebrow='Indicações por dia'
                    icon={TrendingUpRoundedIcon}
                    pointAriaLabel={(point) => `${point.label}: ${formatInteger(point.indicated)} indicações e ${formatInteger(point.concluded)} concluídas`}
                    pointKey={(point) => point.day}
                    rows={dailyIndications}
                    series={chartSeries}
                    title='Indicadas e concluídas no período'
                    xTickLabel={(point) => new Date(`${point.day}T00:00:00`).getDate()}
                />

                <Paper className='bi-aluguel-access-panel bi-aluguel-access-table-panel'>
                    <div className='bi-aluguel-access-panel-heading'>
                        <div>
                            <p>Indicações filtradas</p>
                            <h2>Lista por cliente indicador</h2>
                        </div>
                        <RecommendRoundedIcon />
                    </div>

                    <div className='bi-aluguel-access-grid-shell'>
                        <DataGrid
                            className='bi-aluguel-access-grid'
                            rows={rows}
                            columns={columns}
                            loading={isLoading}
                            disableRowSelectionOnClick
                            localeText={{ noRowsLabel: emptyRowsLabel }}
                            pageSizeOptions={[25, 50, 100]}
                            initialState={{
                                pagination: {
                                    paginationModel: {
                                        pageSize: 50
                                    }
                                },
                                sorting: {
                                    sortModel: [{ field: 'ticketIndicado', sort: 'desc' }]
                                }
                            }}
                        />
                    </div>
                </Paper>
            </section>
        </Box>
    );
};

export default IndicacoesBIGE;
